import {Formik} from 'formik'
import {useEffect, useState} from 'react'
import styled from 'styled-components'

import type {CompanyAdoptionGuideResponse} from '@pleo-io/auxo-ts-models'

import {Accordion, useAccordionState} from '@product-web/telescope-lab/accordion'

import tracking from '~/tracking'

import {ExportGuide} from './guides/export-guide'
import {AdoptionGuides, getGuideToShow} from './guides/helper'
import MoreItem from './more-section/more-item'
import ProTipsItem from './pro-tips-section/pro-tips-item'

import {FeedbackState} from '../feedback/feedback'
import {Unhelpful, UnhelpfulReason} from '../feedback/unhelpful'

const StyledAccordion = styled(Accordion)`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`

interface AdoptionAccordionProps {
    guides: CompanyAdoptionGuideResponse[]
    closeAdoptionCentre: () => void
}

export enum AdoptionAccordionView {
    INITIAL = 'INITIAL',
    UNHELPFUL_FEEDBACK = 'UNHELPFUL_FEEDBACK',
    GUIDE_DETAILS = 'GUIDE_DETAILS',
}

export const AdoptionAccordion = ({guides, closeAdoptionCentre}: AdoptionAccordionProps) => {
    const guide = getGuideToShow(guides)

    const [feedbackState, setFeedbackState] = useState(FeedbackState.None)
    const [view, setView] = useState<AdoptionAccordionView>(AdoptionAccordionView.INITIAL)

    const defaultOpenAccordionId = guide ? ProTipsItem.accordionItemId : MoreItem.accordionItemId

    const accordionState = useAccordionState<'multiple'>([defaultOpenAccordionId])

    const [viewTracked, setViewTracked] = useState(false)

    useEffect(() => {
        if (accordionState.value.includes(ProTipsItem.accordionItemId) && !viewTracked && guide) {
            tracking.adoptionEssentialsCentreActioned({
                action: 'viewed_guide',
                guide: guide.adoptionGuide,
            })
            setViewTracked(true)
        }
    }, [accordionState.value, viewTracked, setViewTracked, guide])

    const values = {
        unhelpful: {
            reason: String(UnhelpfulReason.None),
            otherReason: '',
        },
        helpful: {
            feedback: '',
        },
    }

    const renderGuide = (guideObject: CompanyAdoptionGuideResponse) => {
        switch (guideObject.adoptionGuide) {
            case AdoptionGuides.ExportEssentials:
                return (
                    <ExportGuide
                        view={view}
                        setView={setView}
                        guide={guideObject}
                        feedbackState={feedbackState}
                        setFeedbackState={setFeedbackState}
                    />
                )

            default:
                return null
        }
    }

    return (
        <StyledAccordion type="multiple" {...accordionState} data-testid="adoption-accordion">
            <Formik initialValues={values} onSubmit={() => {}}>
                <>
                    {(guide && renderGuide(guide)) || <MoreItem />}

                    {guide && view === AdoptionAccordionView.UNHELPFUL_FEEDBACK && (
                        <Unhelpful
                            setFeedbackState={setFeedbackState}
                            guideName={guide.adoptionGuide}
                            closeAdoptionCentre={closeAdoptionCentre}
                            setView={setView}
                        />
                    )}
                </>
            </Formik>
        </StyledAccordion>
    )
}
